import { clusterApiUrl } from "@solana/web3.js";
import { WalletAdapterNetwork } from "@solana/wallet-adapter-base";
import {
  ConnectionProvider,
  WalletProvider,
} from "@solana/wallet-adapter-react";
import { WalletModalProvider } from "@solana/wallet-adapter-react-ui";
import { FC, useEffect, useState } from "react";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { GatewayProvider } from "./context/GatewayContext";
import { BleProvider } from "./context/BleContext";
import { NavigationProvider } from "./context/NavigationContext";
import { WalletContextProvider } from "./context/WalletContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "react-hot-toast";
import { AppContent } from './components/AppContent';
import SolanaWalletConnector from './components/SolanaWalletConnector';
import { 
  PhantomWalletAdapter,
  CoinbaseWalletAdapter,
  SolflareWalletAdapter,
} from '@solana/wallet-adapter-wallets';
import { storeOAuthError } from "./components/OAuthErrorHandler";

require("@solana/wallet-adapter-react-ui/styles.css");

// Create a new client
const queryClient = new QueryClient();

// Storage key for tracking manual wallet disconnects
export const WALLET_DISCONNECT_KEY = 'wallet_manually_disconnected';

const App: FC = () => {
  const network = WalletAdapterNetwork.Mainnet;
  const endpoint = clusterApiUrl(network);
  
  // Check if wallet was manually disconnected in the current session
  const [shouldAutoConnect] = useState(() => {
    // Check for and clean up stale localStorage keys
    const localFlag = localStorage.getItem(WALLET_DISCONNECT_KEY) === 'true';
    const sessionFlag = sessionStorage.getItem(WALLET_DISCONNECT_KEY) === 'true';
    
    // If there's a localStorage flag but no sessionStorage flag, it's stale
    if (localFlag && !sessionFlag) {
      localStorage.removeItem(WALLET_DISCONNECT_KEY);
      console.log('Cleared stale localStorage wallet disconnect flag');
    }
    
    return sessionStorage.getItem(WALLET_DISCONNECT_KEY) !== 'true';
  });
  
  // Initialize the wallet adapters
  const wallets = [
    new PhantomWalletAdapter({ appIdentity: { name: "Srcful Dashboard" }, detectWallets: false }),
    new CoinbaseWalletAdapter(),
    new SolflareWalletAdapter(),
  ];

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = storeOAuthError(params);
    
    if (error) {
      // Just store the error and clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ConnectionProvider endpoint={endpoint}>
        <WalletProvider wallets={wallets} autoConnect={shouldAutoConnect}>
          <WalletModalProvider>
            <WalletContextProvider>
              <SolanaWalletConnector />
              <ThemeProvider>
                <GatewayProvider>
                  <BleProvider>
                    <NavigationProvider>
                      <Toaster
                        position="top-center"
                        toastOptions={{
                          style: {
                            background: 'var(--toast-bg, #1D1D1D)',
                            color: 'var(--toast-text, #fff)',
                            borderRadius: '8px',
                            border: 'var(--toast-border, 1px solid #292929)',
                            padding: '12px 16px',
                          },
                          success: {
                            iconTheme: {
                              primary: '#00CC6A',
                              secondary: 'var(--toast-bg, #1D1D1D)',
                            },
                          },
                          error: {
                            iconTheme: {
                              primary: '#FF4747',
                              secondary: 'var(--toast-bg, #1D1D1D)',
                            },
                          },
                          loading: {
                            iconTheme: {
                              primary: '#AAAAAA',
                              secondary: 'var(--toast-bg, #1D1D1D)',
                            },
                          },
                        }}
                      />
                      <AppContent />
                    </NavigationProvider>
                  </BleProvider>
                </GatewayProvider>
              </ThemeProvider>
            </WalletContextProvider>
          </WalletModalProvider>
        </WalletProvider>
      </ConnectionProvider>
    </QueryClientProvider>
  );
};

export default App;
